import { map, isNull, remove, flatMap, reject, endsWith, get, uniq, filter, sortBy } from 'lodash';
/**
   * @ngdoc service
   * @name adjust.statistics.service:Column
   *
   * @description
   * Service used with columns directive within the statistics pages
   *
   */
function Column(Table, Filter, Kpi, User, $q) {
  'ngInject';
  var _persistentColumns = {
    overview: {
      all: ['clicks', 'installs', 'sessions']
    },
    cost: {
      all: ['cost']
    },
    'fraud-prevention': {
      all: ['clicks', 'installs', 'sessions', 'rejected_installs']
    },
    cohorts: {
      all: ['retained_users', 'sessions_per_user'],
      events: ['converted_users', 'events_per_user']
    }
  };
  var _selected = null;

  return {
    getColumns: getColumns,
    getTotals: getTotals,
    getPersistentColumns: getPersistentColumns,
    setColumnsVisibility: setColumnsVisibility,
    setSelected: setSelected,
    getSelected: getSelected,
    filterSelected: filterSelected,
    isAllSelected: isAllSelected,
    orderEvents: orderEvents,
    destroySelected: destroySelected
  };

  /**
   * Replace particular kpi with another one in the list
   *
   * @param {Array} list list of kpis
   * @param {string} original kpi to be replaced
   * @param {string} replacement kpi to replace with
   * @return {Array} list of kpis with corrected values
   * @private
   */
  function _replace(list, original, replacement) {

    return map(list, function(kpi) {

      if (kpi === original + 's' && replacement !== 'click') {
        return replacement + 's';
      }

      if (kpi === original + '_conversion_rate') {
        return replacement + '_conversion_rate';
      }

      return kpi;

    });

  }

  /**
   * @ngdoc method
   * @name getColumns
   * @methodOf adjust.statistics.service:Column
   *
   * @description
   * Get currently set columns
   *
   * @param {Object} options various options
   * @param {Boolean} options.split split events using kpi service or not
   * @param {String} options.attributionType selected attribution type
   * @param {String} options.attributionSource selected attribution source
   * @param {Boolean} options.tmpChange check if there is temporary column selection
   *
   * @returns {Promise|Array} list of columns that are set
   */
  function getColumns(options) {

    options = options || {};

    var isSelectionChanged = options.tmpChange && !isNull(_selected);
    var selectedColumns = isSelectionChanged ? getSelected() : Filter.getParam('columns');
    var promise;

    if (options.attributionType === 'click') {
      selectedColumns = _replace(selectedColumns, 'impression', 'click');
    } else if (options.attributionType === 'impression') {
      selectedColumns = _replace(selectedColumns, 'click', 'impression');
    }

    if (options.attributionSource === 'dynamic') {
      remove(selectedColumns, function(column) {
        return column === 'deattributions';
      });
    }

    if (!isSelectionChanged) {
      Filter.preserveParam('columns', selectedColumns);
    }

    if (options.split) {
      promise = Kpi.splitEvent(selectedColumns);
    } else {
      promise = $q.when({kpis: selectedColumns, events: []});
    }

    return $q.all([
      promise,
      User.getCurrent()
    ])
      .then(([ resp, currentUser ]) => {

        const customEvents = flatMap(resp.events, event => event.indexOf('_') === -1
            ? [event + '_events', event + '_revenue']
            : event);

        const filteredEvents = reject(customEvents, column => endsWith(column, '_revenue')
            && !get(currentUser, 'features.revenue_data.enabled', false));

        const columns = uniq([
          ...resp.kpis,
          ...filteredEvents
        ]);

        if (options.tmpChange) {
          return {
            columns,
            order: selectedColumns
          };
        }

        return columns;

      });

  }

  /**
   * @ngdoc method
   * @name getTotals
   * @methodOf adjust.statistics.service:Column
   *
   * @description
   * Get column total values
   *
   * @returns {Object} total values for each column
   */
  function getTotals() {

    return Table.getTotals();

  }

  /**
   * @ngdoc method
   * @name getPersistentColumns
   * @methodOf adjust.statistics.service:Column
   *
   * @description
   * Get persistent columns
   *
   * @returns {string} [screen=overview] - screen current screen, can be overview, cost, fraud-prevention or cohorts
   * @returns {string} [base=all] - base default is all, but can be events when in cohorts
   */
  function getPersistentColumns(screen, base) {

    screen = screen || 'overview';
    base = base || 'all';

    return angular.copy(_persistentColumns[screen][base]);

  }

  /**
   * Set column visibility depending on two cases:
   * - display columns that are set
   * - if columns are empty display the ones that are non-zero and that are persistent
   * - still display column if it's a cohort-revenue, even if it's empty
   *
   * @param {Array} events kpi/events to show
   * @param {Boolean} hideEmpty hide empty columns
   * @param {Object} params current params
   * @param {String} params.attribution_type current attribution type
   * @param {String} params.attribution_source current attribution source
   * @param {String} params.grouping current grouping
   * @param {String} currentScreen
   *
   */
  function setColumnsVisibility(events, hideEmpty, params = {}, currentScreen = 'overview') {
    var hideConversion = !params.trackerToken && params.grouping && params.grouping !== 'trackers';

    return getColumns({
      split: true,
      attributionType: params.attribution_type,
      attributionSource: params.attribution_source
    }).then(columns => {
      const totals = getTotals();

      return map(events, event => {
        let empty;

        const { sum: cachedSum } = totals[event.token];
        const calculatedSum = Table.getTotal({
          kpi: event.token,
          csv: true
        });
        const isEmptyCustomRevenue = !!event.isCustom && endsWith(event.token, '_revenue') && calculatedSum === 0;
        const isConversionRate = event.name.includes('_conversion_rate');
        const hide = (isConversionRate && hideConversion) || isEmptyCustomRevenue;

        if (!hideEmpty || columns.length) {
          empty = !columns.includes(event.token) && columns.length > 0;
        } else if (hideEmpty) {
          empty = !_persistentColumns[currentScreen].all.includes(event.token) && calculatedSum === 0;
        }

        return {
          ...event,
          hide,
          empty,
          sum: cachedSum
        };
      });
    });
  }

  /**
   * Set temporary selected columns
   *
   * @param {Array} selected temporary selected columns
   */
  function setSelected(selected) {

    _selected = map(selected, function(column) {

      return column.isCustom ? column.token.replace(/_.*/, '') : column.token;

    });

  }

  /**
   * Get list of temporary selected columns and optionally clear
   *
   * @param {boolean=} clear clear the list if necessary
   * @returns {Array} list of selected columns
   */
  function getSelected(clear) {

    var selected = angular.copy(_selected);

    if (clear) {
      destroySelected();
    }

    return selected;

  }

  /**
   * Filter selected columns
   * @param {Array} columns
   * @param {String} ignore column to ignore
   * @returns {Array}
   */
  function filterSelected(columns, ignore) {

    return filter(columns, function(column) {
      return column.selected && !column[ignore];
    });
  }

  /**
   * Check if all elements in the list are selected
   *
   * @param {Array} list
   * @returns {boolean}
   */
  function isAllSelected(list = []) {

    return filter(list, {selected: true }).length === list.length;
  }

  /**
   * Order events by position and persist previous index
   *
   * @param {Array} events
   * @param {Array=} order
   * @returns {Array}
   */
  function orderEvents(events, order = []) {

    order = order && order.length ?
      order :
      Filter.getParam('columns') || [];

    if (!order.length) {
      return events;
    }

    return sortBy(events, function(item) {
      let token  = item.common || item.token;
      let index = order.indexOf(token);

      if (index !== -1) {
        return index;
      }
    });

  }

  /**
   * Destroy temporary selected columns
   */
  function destroySelected() {

    _selected = null;

  }
}

export { Column };
